import { Box, Typography } from '@mui/material'
import Diagram from 'assets/images/pure-play-guidance.png'
import { ResourceHeader, ResourceTitle } from '../components/ResourceComponents'

export default function StrategicFocusGap() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mx: 10,
        my: 6,
      }}>
      <ResourceHeader>Understanding the Strategic Focus Gap (Pure Play %)</ResourceHeader>

      <Typography sx={{ whiteSpace: 'pre-line' }}>
        {`The Strategic Focus Gap helps assess how focused a company is within its own key innovation areas and how that compares to other companies working in the same space.

The gap is calculated using the Pure Play %, which reflects how much of a company’s overall patent portfolio falls within the IP-defined boundary of the target company.
`}
        <ResourceTitle>How It Works</ResourceTitle>
        {`1. We define the IP boundary based on the target company’s patent portfolio.

2. For each competitor, we calculate the percentage of their patents that fall within this boundary, their Pure Play %.

3. The Strategic Focus Gap is the difference between the target company’s Pure Play % and that of either:
 - The closest peer (used in gap-to-peer views)
 - The average of top peers (used in gap-to-mean views)`}
      </Typography>
      <Box
        component="img"
        src={Diagram}
        alt="Diagrama Pure Play"
        sx={{
          width: '100%',
          mx: 'auto',
          my: 2,
          display: 'block',
        }}
      />
      <Typography sx={{ whiteSpace: 'pre-line' }}>
        <ResourceTitle pb={0}>Gap Band Interpretations</ResourceTitle>
        <ResourceTitle>{'>'} 50% — High Gap</ResourceTitle>
        {`What it means:

The target company is highly focused in its core space, while competitors show little overlap. Implications:
– Strong specialisation
– Potential first-mover advantage
– Limited direct competition
– Strategic confidence for investors`}
        <ResourceTitle>10–50% — Moderate Gap</ResourceTitle>
        {`What it means:
  
The target is still more focused than its peers, but there is partial alignment from some competitors. Implications:
– Differentiated, but not unique
– Competitive attention is building – Worth tracking closely`}
        <ResourceTitle>{'<'} 10% — Low Gap</ResourceTitle>
        {`What it means:
Several peers are also highly focused within the same IP boundary. Implications:
– Strong competitive alignment
– Possible pressure on market share or innovation lead – Review defensibility of position`}
        <ResourceTitle>Negative Gap — Peer is More Focused</ResourceTitle>
        {`What it means:
One or more competitors are more focused in the target’s core space than the target itself. Implications:
– The target may be more diversified or less committed to this area
– A competitor could be more strategically aligned or more active in this niche
– Signals potential risk: the target may not lead in the space it appears to define
`}
        <ResourceTitle>Why This Matters for Investors</ResourceTitle>
        {`The Strategic Focus Gap provides an objective view of market positioning:
– High gaps often signal strong strategic control
– Low or negative gaps highlight competitive threats or diffusion
– It gives context around R&D focus, competitor intensity, and innovation concentration, without requiring prior IP expertise`}
      </Typography>
    </Box>
  )
}
